import { ReviewTabs } from "../components/ReviewTabs";
import { Check, CircleCheck, PartyPopper, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EXAMS } from "../data/exams";
import { FLASHCARDS } from "../data/flashcards";
import { flashcardsFor } from "../data/localized";
import { useT } from "../i18n";
import type { ExamId } from "../data/types";
import { Empty, ProgressBar, useDocumentTitle } from "../components/ui";
import { recordCard } from "../lib/progress";
import { shuffle } from "../lib/random";
import { isDue, isMastered } from "../lib/srs";
import { progressStore, useProgress } from "../lib/store";

type Mode = "due" | "all";

export function FlashcardsPage() {
  const t = useT();
  useDocumentTitle("Flashcards");
  const p = useProgress();
  const cardsL = flashcardsFor(p.lang);
  const [exam, setExam] = useState<ExamId | "all">("all");
  const [mode, setMode] = useState<Mode>("due");
  const [deck, setDeck] = useState<string[]>([]);
  const [pos, setPos] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [stats, setStats] = useState({ knew: 0, again: 0 });

  const pool = useMemo(() => FLASHCARDS.filter((c) => exam === "all" || c.exam === exam), [exam]);

  const buildDeck = useCallback(() => {
    const now = Date.now();
    // Đọc trực tiếp từ store: bộ thẻ chỉ dựng lại khi đổi bộ lọc, không phải mỗi lần chấm thẻ.
    const cards = progressStore.get().cards;
    const picked = pool.filter((c) => mode === "all" || !cards[c.id] || isDue(cards[c.id], now));
    setDeck(shuffle(picked.map((c) => c.id)));
    setPos(0);
    setFlipped(false);
    setStats({ knew: 0, again: 0 });
  }, [pool, mode]);

  useEffect(buildDeck, [buildDeck]);

  const card = cardsL.find((c) => c.id === deck[pos]);
  const mastered = pool.filter((c) => isMastered(p.cards[c.id])).length;

  const answer = useCallback(
    (knew: boolean) => {
      if (!card) return;
      progressStore.update((x) => recordCard(x, card.id, knew, Date.now()));
      setStats((s) => (knew ? { ...s, knew: s.knew + 1 } : { ...s, again: s.again + 1 }));
      setFlipped(false);
      setPos((i) => i + 1);
    },
    [card],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!card || e.metaKey || e.ctrlKey) return;
      if (e.key === " ") {
        e.preventDefault();
        setFlipped((f) => !f);
      } else if (flipped && (e.key === "1" || e.key.toLowerCase() === "j")) answer(false);
      else if (flipped && (e.key === "2" || e.key.toLowerCase() === "k")) answer(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, flipped, answer]);

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <h1>Flashcards</h1>
      <ReviewTabs />
      <p className="muted">{t(`${FLASHCARDS.length} thẻ khái niệm & con số cốt lõi. Lật thẻ, tự đánh giá — thẻ “chưa nhớ” sẽ quay lại sớm hơn.`, `${FLASHCARDS.length} cards on core concepts and numbers. Flip, rate yourself — cards you missed come back sooner.`)}</p>

      <div className="card stack">
        <div className="pill-group" role="group" aria-label={t("Lọc theo chứng chỉ", "Filter by certification")}>
          <button type="button" className="pill" aria-pressed={exam === "all"} onClick={() => setExam("all")}>{t("Tất cả", "All")}</button>
          {EXAMS.map((e) => (
            <button key={e.id} type="button" className="pill" aria-pressed={exam === e.id} onClick={() => setExam(e.id)}>{e.code}</button>
          ))}
        </div>
        <div className="row between">
          <div className="pill-group" role="group" aria-label={t("Chế độ", "Mode")}>
            <button type="button" className="pill" aria-pressed={mode === "due"} onClick={() => setMode("due")}>{t("Đến hạn & mới", "Due & new")}</button>
            <button type="button" className="pill" aria-pressed={mode === "all"} onClick={() => setMode("all")}>{t("Toàn bộ", "All cards")}</button>
          </div>
          <span className="small muted">{t("Thành thạo", "Mastered")} {mastered}/{pool.length}</span>
        </div>
        <ProgressBar value={deck.length ? (pos / deck.length) * 100 : 100} label={t("Tiến độ bộ thẻ", "Deck progress")} />
      </div>

      <div className="mt">
        {card ? (
          <>
            <div
              className={`flip${flipped ? " flipped" : ""}`}
              onClick={() => setFlipped((f) => !f)}
              role="button"
              tabIndex={0}
              aria-label={flipped ? t("Mặt sau, bấm để lật lại", "Back side, click to flip") : t("Mặt trước, bấm để lật", "Front side, click to flip")}
              onKeyDown={(e) => e.key === "Enter" && setFlipped((f) => !f)}
            >
              <div className="flip-inner">
                <div className="flip-face" aria-hidden={flipped}>
                  <span className="chip small">{card.topic}</span>
                  <p className="front-text mt">{card.front}</p>
                  <span className="tiny muted">{t("Bấm hoặc nhấn", "Click or press")} <span className="kbd">Space</span> {t("để lật", "to flip")}</span>
                </div>
                <div className="flip-face back" aria-hidden={!flipped}>
                  <p className="back-text mb-0">{card.back}</p>
                </div>
              </div>
            </div>
            <div className="row mt" style={{ justifyContent: "center" }}>
              <button type="button" className="btn lg" disabled={!flipped} onClick={() => answer(false)}><X size={18} aria-hidden="true" /> {t("Chưa nhớ", "Again")} <span className="kbd">1</span></button>
              <button type="button" className="btn lg primary" disabled={!flipped} onClick={() => answer(true)}><Check size={18} aria-hidden="true" /> {t("Đã nhớ", "Got it")} <span className="kbd">2</span></button>
            </div>
            <p className="tiny muted mt" style={{ textAlign: "center" }}>{t("Thẻ", "Card")} {pos + 1}/{deck.length}</p>
          </>
        ) : (
          <Empty icon={deck.length ? PartyPopper : CircleCheck} title={deck.length ? t("Hoàn thành bộ thẻ!", "Deck complete!") : t("Không có thẻ nào đến hạn", "No cards due")}>
            {deck.length > 0 && <p>{t("Đã nhớ", "Got it")} {stats.knew} · {t("cần ôn lại", "to review")} {stats.again}</p>}
            <div className="row" style={{ justifyContent: "center" }}>
              <button type="button" className="btn primary" onClick={() => { setMode("all"); buildDeck(); }}>{t("Ôn toàn bộ thẻ", "Review all cards")}</button>
            </div>
          </Empty>
        )}
      </div>
    </div>
  );
}
